import { Card, Divider, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import FormEmailDomainField from '../../../FormFields/FormEmailDomainField';
import FormStaffInfoField from '../../../FormFields/StaffInfoField/FormStaffInfoField';
import FormStaffInfoFieldWithAddMore from '../../../FormFields/StaffInfoField/FormStaffInfoFieldWithAddMore';
import FormStaffInfoFieldWithButtons from '../../../FormFields/StaffInfoField/FormStaffInfoFieldWithButtons';
import FormStaffInfoFieldWithContracted from '../../../FormFields/StaffInfoField/FormStaffInfoFieldWithContracted';
import FormStaffInfoFieldWithDiffDept from '../../../FormFields/StaffInfoField/FormStaffInfoFieldWithDiffDept';
import { formCardStyle } from '../../../TransitionStyles';
import { OPERATIONS } from '../../../formQuestionsEnum';
import { STAFF_SUB_TYPES } from '../../../staffSubTypesEnum';

StaffForm.propTypes = {
  permissions: PropTypes.object,
};

export default function StaffForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);

  const staffSubTypes = useSelector((state) => state.transitions?.data?.staffSubTypes || []);
  const allowedValues = [STAFF_SUB_TYPES.RNAC, STAFF_SUB_TYPES.LNAC];
  const mdsSubTypes = staffSubTypes.filter((subType) => allowedValues.includes(subType.value));

  const ADMINISTRATIVE_STAFF_FIELDS = [
    {
      ...OPERATIONS.ADMINISTRATOR,
      component: FormStaffInfoFieldWithContracted,
      questionTitle: 'Current administrator',
    },
    {
      ...OPERATIONS.ASSISTANT_ADMINISTRATOR,
      component: FormStaffInfoFieldWithDiffDept,
      questionTitle: 'Current assistant administrator',
    },
    {
      ...OPERATIONS.DIRECTOR_OF_NURSING,
      component: FormStaffInfoFieldWithContracted,
      questionTitle: 'Current director of nursing',
    },
    { ...OPERATIONS.ADON, component: FormStaffInfoField, questionTitle: 'Current ADON' },
    {
      ...OPERATIONS.MDSRNACLNAC_DIRECTOR,
      component: FormStaffInfoFieldWithButtons,
      attributes: { options: mdsSubTypes },
      questionTitle: 'Current MDS-RNAC/LNAC director',
    },
    {
      ...OPERATIONS.MAINTENANCE_DIRECTOR,
      component: FormStaffInfoFieldWithAddMore,
      questionTitle: 'Current maintenance director',
    },
    {
      ...OPERATIONS.HOUSEKEEPING_DIRECTOR,
      component: FormStaffInfoField,
      questionTitle: 'Current housekeeping director',
    },
    { ...OPERATIONS.DIETARY_DIRECTOR, component: FormStaffInfoField, questionTitle: 'Current dietary director' },
    { ...OPERATIONS.DIETICIAN, component: FormStaffInfoFieldWithContracted, questionTitle: 'Current dietician' },
    {
      ...OPERATIONS.CENTRAL_SUPPLIES_DIRECTOR,
      component: FormStaffInfoFieldWithDiffDept,
      questionTitle: 'Current central supplies director',
    },
    {
      ...OPERATIONS.INFECTION_CONTROL,
      component: FormStaffInfoFieldWithDiffDept,
      questionTitle: 'Current infection control',
    },
    { ...OPERATIONS.SCHEDULER, component: FormStaffInfoField, questionTitle: 'Current scheduler' },
    { ...OPERATIONS.ACTIVITIES_DIRECTOR, component: FormStaffInfoField, questionTitle: 'Current activities director' },
    {
      ...OPERATIONS.BUSINESS_OFFICE_MANAGER,
      component: FormStaffInfoField,
      questionTitle: 'Current business office manager',
    },
    { ...OPERATIONS.HR_DIRECTOR, component: FormStaffInfoFieldWithDiffDept, questionTitle: 'Current HR director' },
    {
      ...OPERATIONS.SOCIAL_SERVICES_DIRECTOR,
      component: FormStaffInfoFieldWithDiffDept,
      questionTitle: 'Current social services director',
    },
    {
      ...OPERATIONS.MEDICAL_RECORDS_DIRECTOR,
      component: FormStaffInfoFieldWithDiffDept,
      questionTitle: 'Current medical records director',
    },
    { ...OPERATIONS.UNIT_MANAGER, component: FormStaffInfoFieldWithAddMore, questionTitle: 'Current unit manager' },
    { ...OPERATIONS.MARKETER, component: FormStaffInfoField, questionTitle: 'Current marketer' },
    { ...OPERATIONS.CLINICAL_LIAISON, component: FormStaffInfoField, questionTitle: 'Current clinical liaison' },
    { ...OPERATIONS.ADMISSIONS_DIRECTOR, component: FormStaffInfoField, questionTitle: 'Current admissions director' },
  ];

  const FORM_FIELD_IDS = [...ADMINISTRATIVE_STAFF_FIELDS.map((field) => field.id), OPERATIONS.EMAIL_DOMAIN.id];

  const showForm = FORM_FIELD_IDS.some((id) => id in permissions);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Department Heads and Key Staff
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        {OPERATIONS.EMAIL_DOMAIN.id in permissions && (
          <FormEmailDomainField
            fieldName={OPERATIONS.EMAIL_DOMAIN.fieldName}
            fieldAttributes={{
              isEditable: permissions[OPERATIONS.EMAIL_DOMAIN.id],
              questionTitle: 'Email domain',
            }}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
          />
        )}
        {ADMINISTRATIVE_STAFF_FIELDS.map((field) => {
          if (!(field.id in permissions)) return null;
          const Component = field.component;
          return (
            <React.Fragment key={field.id}>
              <Component
                fieldName={field.fieldName}
                fieldAttributes={{
                  isEditable: permissions[field.id],
                  questionTitle: field.questionTitle,
                  fieldName: field.fieldName,
                  ...field.attributes,
                }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
              <Divider sx={{ mb: 2, mt: 2, borderWidth: 1 }} />
            </React.Fragment>
          );
        })}
      </Card>
    )
  );
}
